/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Box, Button, FormControl, Grid, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import CustomCountryCode from "@components/CustomContryCode/CustomContryCode";
import { PayloadPersonPatch } from "@interfaces/index";
import { InactivityHandler } from "@components/InactivityHandler";
import { usePatchPersonMutation, useLazyGetPersonByIdQuery } from "@api/personasApi";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, PersonaStructure } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import { EMAIL, RFC_PFAE } from "@utils/expreciones";

const InformacionRepresentante: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const application = useSelector((state: RootState) => state.register.application);
  const personAval = useSelector((state: RootState) => state.bofu.aval);
  const representanteId = useSelector((state: RootState) => state.bofu.information_representante_id);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [personSelected, setPersonSelected] = useState<PersonaStructure | null>(null);

  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [patchPerson] = usePatchPersonMutation();
  const [getApplicationById] = useLazyGetLoanApplicationByIdQuery();

  const goBack = () => {
    navigate("/producto/creditosimple/docs_representante");
  };

  const formik = useFormik({
    initialValues: {
      rfc: "",
      countryCode: "+52",
      telefono: "",
      email: "",
    },
    validationSchema: yup.object({
      rfc: yup.string().required("Campo requerido").matches(RFC_PFAE, "Formato incorrecto, inténtalo de nuevo"),
      telefono: yup
        .string()
        .required("Requerido")
        .matches(/^[0-9]{10}$/, "Ingresa 10 dígitos"),
      email: yup
        .string()
        .required("Campo requerido")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!representanteId) {
          throw new Error("No se encontró el representante legal");
        }

        const payload_person: PayloadPersonPatch = {
          rfc: values.rfc,
          code_phone_number: values.countryCode,
          phone_number: values.telefono,
          email: values.email,
          information_person_completed: true,
          personal_information_status: "COMPLETO",
        };

        await patchPerson({
          personId: representanteId,
          body: payload_person,
        }).unwrap();

        goBack();
      } catch (error) {
        const api_error = error as ApiError;

        if (api_error.status === 409) {
          formik.setFieldError("email", "El correo electrónico ya se encuentra registrado");
          return;
        }

        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart1-submit",
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const guardadoParcialP1 = async (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    try {
      formik.handleBlur(e);

      if (!representanteId) {
        return;
      }

      const { countryCode: countryCodeError, telefono: telefonoError, email: emailError } = formik.errors;
      if (countryCodeError || telefonoError || emailError) {
        return;
      }

      const payload_person: PayloadPersonPatch = {
        code_phone_number: formik.values.countryCode,
        phone_number: formik.values.telefono,
        email: formik.values.email,
      };

      await patchPerson({
        personId: representanteId,
        body: payload_person,
      }).unwrap();
    } catch (error) {
      console.error("Error guardando parcial:", error);
    }
  };

  const fetchInitialData = async () => {
    try {
      setIsLoading(true);

      if (!application) {
        throw new Error("Solicitud no encontrada");
      }

      if (!representanteId) {
        throw new Error("No se encontró el representante legal");
      }

      const [loan_application, person_representante] = await Promise.all([
        getApplicationById(application.id).unwrap(),
        getPersonById(representanteId).unwrap(),
      ]);

      if (!person_representante || !loan_application) {
        goBack();
        return;
      }

      let rfc_to_set = person_representante.rfc || "";

      if (!rfc_to_set) {
        if (personAval && personAval.id === representanteId) {
          rfc_to_set = personAval.rfc || "";
        }
      }

      setPersonSelected(person_representante);

      formik.setFieldValue("rfc", rfc_to_set);
      formik.setFieldValue("countryCode", person_representante.code_phone_number || "+52");
      formik.setFieldValue("telefono", person_representante.phone_number || "");
      formik.setFieldValue("email", person_representante.email || "");

      setTimeout(() => {
        formik.validateForm();
      }, 200);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "SolicitudCreditoPart1-initial_fetch",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [application, representanteId]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del representante legal
          </Typography>

          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            mt={2}
          >
            <strong>1</strong> de 1
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            {personSelected?.name || ""} {personSelected?.last_name || ""} {personSelected?.last_name_2 || ""}
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <FormControl fullWidth>
              <TextField
                fullWidth
                id="rfc"
                name="rfc"
                label="RFC"
                required
                value={formik.values.rfc}
                onBlur={guardadoParcialP1}
                onChange={formik.handleChange}
                error={formik.touched.rfc && Boolean(formik.errors.rfc)}
                helperText={formik.touched.rfc ? formik.errors.rfc : ""}
                margin="normal"
                disabled={isLoading || formik.isSubmitting}
                inputProps={{ maxLength: 13 }}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={4} sm={4} md={4}>
                  <CustomCountryCode
                    label="Código"
                    name="countryCode"
                    value={formik.values.countryCode}
                    onChange={(e) => formik.setFieldValue("countryCode", e.target.value)}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </Grid>
                <Grid item xs={8} sm={8} md={8}>
                  <TextField
                    type="text"
                    id="telefono"
                    name="telefono"
                    label="Teléfono (10 dígitos)"
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                    }}
                    fullWidth
                    required
                    value={formik.values.telefono}
                    onChange={(event) => {
                      formik.setFieldValue("telefono", event.target.value.replace(/[^0-9]/g, ""));
                    }}
                    onBlur={guardadoParcialP1}
                    error={formik.touched.telefono && Boolean(formik.errors.telefono)}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </Grid>
              </Grid>
            </FormControl>

            <FormControl fullWidth>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Correo electrónico"
                required
                value={formik.values.email}
                onBlur={guardadoParcialP1}
                onChange={(e) => {
                  const value_to_set = (e.target.value || "").trim().toLowerCase();
                  formik.setFieldValue("email", value_to_set);
                }}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email ? formik.errors.email : ""}
                margin="normal"
                disabled={isLoading || formik.isSubmitting}
                inputProps={{ maxLength: 100 }}
              />
            </FormControl>

            <Grid container columnSpacing={1} my={4}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="blue_outlined"
                  sx={{ padding: "0", height: "100%" }}
                  onClick={goBack}
                  disabled={formik.isSubmitting}
                >
                  Regresar al inicio
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  color="primary"
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionRepresentante;
